import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  Pagination,
  Stack,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Container,
} from "@mui/material";
import {
  BsCameraVideoFill,
  BsClockFill,
  BsWebcam,
  BsSearch,
  BsEyeFill,
  BsEyeSlashFill,
  BsClipboard,
  BsCheck2,
  BsBoxArrowUpRight,
  BsArrowCounterclockwise,
  BsBuilding,
  BsGridFill,
  BsListUl,
  BsFileEarmarkPdfFill,
  BsCheckCircleFill,
  BsPencilFill,
  BsTrashFill,
} from "react-icons/bs";
import { LIVECAMALL, TIMELAPSEALL, COMPANY } from "../../services/actions";
import Breadcrumb from "../../extra2/Breadcrumb";
import RDialog from "../extra/RDialog";

const PRESET_TASKS = [
  "Password Changed",
  "Firmware Updated",
  "IP / Network Checked",
  "Maintenance Done",
  "Security Audit",
  "DNS / Port Forwarded",
  "Lens Cleaned",
];

export default function AdminCameraView() {
  const dispatch = useDispatch();
  const livecamallState = useSelector((state) => state?.livecamallState);
  const timelapseallState = useSelector((state) => state?.timelapseallState);
  const companyState = useSelector((state) => state?.companyState);

  // Filter States
  const [selectedType, setSelectedType] = useState("ALL");
  const [selectedCompany, setSelectedCompany] = useState("ALL");
  const [pwStatusFilter, setPwStatusFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("table");

  // Persistent Pagination (stored in localStorage)
  const [page, setPage] = useState(() => {
    try {
      const saved = localStorage.getItem("admin_camera_page");
      return saved ? Math.max(1, parseInt(saved, 10)) : 1;
    } catch (e) {
      return 1;
    }
  });
  const [pageSize, setPageSize] = useState(() => {
    try {
      const saved = localStorage.getItem("admin_camera_pagesize");
      return saved ? parseInt(saved, 10) : 25;
    } catch (e) {
      return 25;
    }
  });

  const handlePageChange = (newPage) => {
    setPage(newPage);
    try {
      localStorage.setItem("admin_camera_page", String(newPage));
    } catch (e) {}
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
    try {
      localStorage.setItem("admin_camera_pagesize", String(newSize));
      localStorage.setItem("admin_camera_page", "1");
    } catch (e) {}
  };

  // Customizable Task Title & Local Storage Persistence
  const [taskTitle, setTaskTitle] = useState(() => {
    try {
      return localStorage.getItem("admin_camera_task_title") || "Password Changed";
    } catch (e) {
      return "Password Changed";
    }
  });
  const [tempTaskTitle, setTempTaskTitle] = useState(taskTitle);
  const [openTaskDialog, setOpenTaskDialog] = useState(false);

  // Task Status Tracking (stored in localStorage)
  const [taskStatus, setTaskStatus] = useState(() => {
    try {
      const saved = localStorage.getItem("admin_camera_task_status");
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  const toggleTaskStatus = (id) => {
    setTaskStatus((prev) => {
      const updated = {
        ...prev,
        [id]: !prev[id],
      };
      try {
        localStorage.setItem("admin_camera_task_status", JSON.stringify(updated));
      } catch (e) {}
      return updated;
    });
  };

  const handleSaveTaskTitle = (newTitle) => {
    const formatted = (newTitle || "").trim() || "Task Status";
    setTaskTitle(formatted);
    try {
      localStorage.setItem("admin_camera_task_title", formatted);
    } catch (e) {}
    setOpenTaskDialog(false);
  };

  const handleClearTaskMarks = () => {
    if (window.confirm(`Are you sure you want to clear all checkmarks for "${taskTitle}"?`)) {
      setTaskStatus({});
      try {
        localStorage.removeItem("admin_camera_task_status");
      } catch (e) {}
      setOpenTaskDialog(false);
    }
  };

  // Visibility toggles for passwords & clipboard
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [copiedId, setCopiedId] = useState(null);

  // PDF Export for current filtered cameras including dynamic task status
  const handleExportPDF = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Please allow popups to export the PDF.");
      return;
    }

    const dateStr = new Date().toLocaleString();
    const typeLabel = selectedType === "ALL" ? "All Types" : selectedType;
    const compLabel = selectedCompany === "ALL" ? "All Companies" : selectedCompany;
    const searchLabel = searchQuery.trim() ? searchQuery : "None";
    const pwFilterLabel = pwStatusFilter === "ALL" ? "All" : (pwStatusFilter === "CHANGED" ? `Done Only` : "Pending Only");

    const totalUpdated = filteredCameras.filter((c) => taskStatus[c.id]).length;

    const rowsHtml = filteredCameras
      .map((cam, idx) => {
        const ipDisplay = cam.ip_address
          ? `${cam.ip_address}${cam.http_port && !["80", "443", ""].includes(String(cam.http_port)) ? `:${cam.http_port}` : ""}`
          : "-";
        const cType = cam.camera_type || "TIMELAPSE";
        const statusText = cam.active !== false ? "Active" : "Inactive";
        const statusClass = cam.active !== false ? "status-active" : "status-inactive";
        const isChanged = Boolean(taskStatus[cam.id]);

        return `
          <tr>
            <td style="text-align: center; color: #64748b; font-weight: bold;">${idx + 1}</td>
            <td>
              <div style="font-weight: 700; color: #0f172a;">${cam.camera_name || "Unnamed Camera"}</div>
              ${cam.project_name ? `<div style="font-size: 10px; color: #64748b;">${cam.project_name}</div>` : ""}
            </td>
            <td><span class="type-badge">${cType}</span></td>
            <td><strong>${cam.company_name || "-"}</strong></td>
            <td><span class="mono">${ipDisplay}</span></td>
            <td><span class="mono">${cam.username || "-"}</span></td>
            <td><span class="mono">${cam.password || "-"}</span></td>
            <td style="text-align: center;">
              ${
                isChanged
                  ? `<span class="pw-changed">✓ ${taskTitle.toUpperCase()}</span>`
                  : `<span class="pw-pending">Pending</span>`
              }
            </td>
            <td>${cam.brand_name || "-"}</td>
            <td style="text-align: center;"><span class="${statusClass}">${statusText}</span></td>
          </tr>
        `;
      })
      .join("");

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Camera_Registry_${new Date().toISOString().slice(0, 10)}</title>
        <meta charset="utf-8" />
        <style>
          @page {
            size: A4 landscape;
            margin: 8mm;
          }
          * {
            box-sizing: border-box;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          }
          body {
            margin: 0;
            padding: 12px;
            color: #1e293b;
            background: #ffffff;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 2px solid #0c2a45;
            padding-bottom: 8px;
            margin-bottom: 10px;
          }
          .title-area h1 {
            margin: 0;
            font-size: 18px;
            color: #0c2a45;
            font-weight: 800;
          }
          .title-area p {
            margin: 2px 0 0 0;
            font-size: 11px;
            color: #64748b;
          }
          .meta-info {
            text-align: right;
            font-size: 10.5px;
            color: #475569;
          }
          .filter-bar {
            background-color: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            padding: 6px 10px;
            margin-bottom: 12px;
            font-size: 10.5px;
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
          }
          .filter-item strong {
            color: #334155;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 10px;
          }
          thead tr {
            background-color: #0c2a45 !important;
            color: #ffffff !important;
          }
          th {
            padding: 6px 5px;
            text-align: left;
            font-weight: 700;
            border: 1px solid #1e3a5f;
            letter-spacing: 0.3px;
          }
          td {
            padding: 5px 5px;
            border: 1px solid #e2e8f0;
            vertical-align: middle;
          }
          tr:nth-child(even) {
            background-color: #f8fafc;
          }
          .mono {
            font-family: Consolas, "Courier New", Courier, monospace;
            font-weight: 600;
            color: #0f172a;
          }
          .type-badge {
            display: inline-block;
            padding: 2px 5px;
            border-radius: 4px;
            font-size: 9px;
            font-weight: 700;
            background: #eef2ff;
            color: #3730a3;
          }
          .pw-changed {
            display: inline-block;
            padding: 2px 5px;
            border-radius: 4px;
            font-size: 9px;
            font-weight: 800;
            background: #dcfce7;
            color: #15803d;
            border: 1px solid #86efac;
          }
          .pw-pending {
            display: inline-block;
            padding: 2px 5px;
            border-radius: 4px;
            font-size: 9px;
            font-weight: 600;
            background: #f1f5f9;
            color: #64748b;
          }
          .status-active {
            display: inline-block;
            padding: 2px 5px;
            border-radius: 4px;
            font-size: 9px;
            font-weight: 700;
            background: #ecfdf5;
            color: #047857;
          }
          .status-inactive {
            display: inline-block;
            padding: 2px 5px;
            border-radius: 4px;
            font-size: 9px;
            font-weight: 700;
            background: #f1f5f9;
            color: #64748b;
          }
          .no-print-bar {
            margin-bottom: 12px;
            padding: 8px 12px;
            background: #f0fdf4;
            border: 1px solid #bbf7d0;
            border-radius: 6px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .print-btn {
            background: #0c2a45;
            color: white;
            border: none;
            padding: 6px 14px;
            border-radius: 6px;
            font-weight: bold;
            cursor: pointer;
            font-size: 12px;
          }
          .print-btn:hover {
            background: #17426c;
          }
          @media print {
            .no-print-bar {
              display: none !important;
            }
            body {
              padding: 0;
            }
            tr {
              page-break-inside: avoid;
              break-inside: avoid;
            }
            thead {
              display: table-header-group;
            }
          }
        </style>
      </head>
      <body>
        <div class="no-print-bar">
          <span style="font-size: 12px; color: #166534; font-weight: 700;">📄 Printable Camera Registry Report (Includes ${taskTitle} Marks)</span>
          <button class="print-btn" onclick="window.print()">🖨️ Print / Save as PDF</button>
        </div>

        <div class="header">
          <div class="title-area">
            <h1>📷 Camera Registry & Credentials Report</h1>
            <p>Confidential • Administrator Audit & ${taskTitle} Tracking</p>
          </div>
          <div class="meta-info">
            <div><strong>Generated:</strong> ${dateStr}</div>
            <div><strong>Total Cameras:</strong> ${filteredCameras.length}</div>
            <div><strong>${taskTitle}:</strong> ${totalUpdated} / ${filteredCameras.length}</div>
          </div>
        </div>

        <div class="filter-bar">
          <div class="filter-item"><strong>Camera Type:</strong> ${typeLabel}</div>
          <div class="filter-item"><strong>Company:</strong> ${compLabel}</div>
          <div class="filter-item"><strong>${taskTitle} Filter:</strong> ${pwFilterLabel}</div>
          <div class="filter-item"><strong>Search Query:</strong> ${searchLabel}</div>
          <div class="filter-item"><strong>Total Matching:</strong> ${filteredCameras.length} cameras</div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 25px; text-align: center;">#</th>
              <th>Camera Name / Project</th>
              <th>Type</th>
              <th>Company</th>
              <th>IP Address & Port</th>
              <th>Username</th>
              <th>Password</th>
              <th style="width: 85px; text-align: center;">${taskTitle}</th>
              <th>Brand</th>
              <th style="width: 55px; text-align: center;">Status</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml || `<tr><td colspan="10" style="text-align: center; padding: 20px; color: #64748b;">No cameras found matching filters</td></tr>`}
          </tbody>
        </table>

        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
            }, 300);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  // Load Network / Live cameras, Timelapse cameras, and Companies
  const loadData = () => {
    dispatch({ type: `${LIVECAMALL}_GET`, payload: 0 });
    dispatch({ type: `${TIMELAPSEALL}_GET`, payload: 0 });
    dispatch({ type: `${COMPANY}_GET`, payload: 0 });
  };

  useEffect(() => {
    loadData();
  }, []);

  // Merge cameras from livecam ("Network") and timelapse without duplication (strictly by cam.id)
  const rawCameras = useMemo(() => {
    const map = new Map();
    const liveCams = livecamallState?.data?.results || (Array.isArray(livecamallState?.data) ? livecamallState.data : []) || [];
    const tlCams = timelapseallState?.data?.results || (Array.isArray(timelapseallState?.data) ? timelapseallState.data : []) || [];

    // Process live / network cameras first (these have full IP, username, password credentials)
    liveCams.forEach((cam) => {
      if (cam && cam.id) {
        map.set(cam.id, {
          ...cam,
          _uid: `cam_${cam.id}`,
          camera_type: cam.camera_type || "LIVECAMERA",
        });
      }
    });

    // Process timelapse cameras (merge if already present, or insert if new)
    tlCams.forEach((cam) => {
      if (cam && cam.id) {
        if (map.has(cam.id)) {
          const existing = map.get(cam.id);
          map.set(cam.id, {
            ...cam,
            ...existing, // keep existing username, password, IP credentials if present
            _uid: `cam_${cam.id}`,
            camera_type: existing.camera_type || cam.camera_type || "TIMELAPSE",
          });
        } else {
          map.set(cam.id, {
            ...cam,
            _uid: `cam_${cam.id}`,
            camera_type: cam.camera_type || "TIMELAPSE",
          });
        }
      }
    });

    return Array.from(map.values());
  }, [livecamallState, timelapseallState]);

  const companiesList = useMemo(() => {
    return companyState?.data?.results || (Array.isArray(companyState?.data) ? companyState.data : []) || [];
  }, [companyState]);

  // Filtering across Camera Type, Company, Task Status, and Search
  const filteredCameras = useMemo(() => {
    return rawCameras.filter((cam) => {
      // 1. Camera Type Filter
      if (selectedType !== "ALL") {
        const cType = (cam.camera_type || "").toUpperCase();
        if (selectedType === "TIMELAPSE" && !cType.includes("TIMELAPSE")) return false;
        if (selectedType === "LIVECAMERA" && !cType.includes("LIVECAMERA") && !cType.includes("LIVE")) return false;
        if (selectedType === "LIVETIMELAPSE" && cType !== "LIVETIMELAPSE") return false;
        if (selectedType === "AICAMERA" && !cType.includes("AI")) return false;
      }

      // 2. Company Name Filter
      if (selectedCompany !== "ALL") {
        const compId = cam.company || cam.company_id;
        const compName = (cam.company_name || "").toLowerCase();
        if (typeof selectedCompany === "number" && compId !== selectedCompany) {
          return false;
        } else if (typeof selectedCompany === "string" && selectedCompany !== "ALL") {
          if (compName !== selectedCompany.toLowerCase() && String(compId) !== selectedCompany) {
            return false;
          }
        }
      }

      // 3. Task Status Filter (Frontend local storage state)
      if (pwStatusFilter !== "ALL") {
        const isUpdated = Boolean(taskStatus[cam.id]);
        if (pwStatusFilter === "CHANGED" && !isUpdated) return false;
        if (pwStatusFilter === "PENDING" && isUpdated) return false;
      }

      // 4. Search Query Filter (Camera Name, IP, Username, Brand)
      if (searchQuery.trim() !== "") {
        const q = searchQuery.toLowerCase().trim();
        const camName = (cam.camera_name || "").toLowerCase();
        const ip = (cam.ip_address || "").toLowerCase();
        const user = (cam.username || "").toLowerCase();
        const comp = (cam.company_name || "").toLowerCase();
        const brand = (cam.brand_name || "").toLowerCase();

        if (
          !camName.includes(q) &&
          !ip.includes(q) &&
          !user.includes(q) &&
          !comp.includes(q) &&
          !brand.includes(q)
        ) {
          return false;
        }
      }

      return true;
    });
  }, [rawCameras, selectedType, selectedCompany, pwStatusFilter, taskStatus, searchQuery]);

  // Pagination calculation
  const totalPages = Math.ceil(filteredCameras.length / pageSize) || 1;
  const paginatedCameras = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredCameras.slice(start, start + pageSize);
  }, [filteredCameras, page, pageSize]);

  const togglePasswordVisibility = (uid) => {
    setVisiblePasswords((prev) => ({
      ...prev,
      [uid]: !prev[uid],
    }));
  };

  const copyToClipboard = (text, idKey) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedId(idKey);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleResetFilters = () => {
    setSelectedType("ALL");
    setSelectedCompany("ALL");
    setPwStatusFilter("ALL");
    setSearchQuery("");
    handlePageChange(1);
  };

  const getTypeChip = (typeStr) => {
    const t = (typeStr || "TIMELAPSE").toUpperCase();
    if (t === "LIVETIMELAPSE") {
      return (
        <Chip
          size="small"
          icon={<BsClockFill size={10} style={{ color: "#4338ca" }} />}
          label="Live TL"
          sx={{ bgcolor: "#eef2ff", color: "#4338ca", fontWeight: 700, fontSize: "0.68rem", height: 20, borderRadius: 1 }}
        />
      );
    } else if (t === "LIVECAMERA" || t === "LIVE") {
      return (
        <Chip
          size="small"
          icon={<BsCameraVideoFill size={10} style={{ color: "#047857" }} />}
          label="Live"
          sx={{ bgcolor: "#ecfdf5", color: "#047857", fontWeight: 700, fontSize: "0.68rem", height: 20, borderRadius: 1 }}
        />
      );
    } else if (t === "AICAMERA" || t.includes("AI")) {
      return (
        <Chip
          size="small"
          icon={<BsWebcam size={10} style={{ color: "#6b21a8" }} />}
          label="AI Cam"
          sx={{ bgcolor: "#faf5ff", color: "#6b21a8", fontWeight: 700, fontSize: "0.68rem", height: 20, borderRadius: 1 }}
        />
      );
    }
    return (
      <Chip
        size="small"
        icon={<BsClockFill size={10} style={{ color: "#b45309" }} />}
        label="Timelapse"
        sx={{ bgcolor: "#fef3c7", color: "#b45309", fontWeight: 700, fontSize: "0.68rem", height: 20, borderRadius: 1 }}
      />
    );
  };

  const buildCameraWebUrl = (cam) => {
    if (!cam || !cam.ip_address) return "#";
    // Clean raw IP / host: strip any leading https://, http://, whitespace and trailing slashes
    const rawIp = String(cam.ip_address).trim().replace(/^https?:\/\//i, "").replace(/\/+$/, "");
    
    // Always start with http:// for camera IP web interfaces
    if (rawIp.includes(":")) {
      return `http://${rawIp}`;
    }
    
    const port =
      cam.http_port && !["80", "443", ""].includes(String(cam.http_port).trim())
        ? `:${String(cam.http_port).trim()}`
        : "";
        
    return `http://${rawIp}${port}`;
  };

  // Count Statistics
  const stats = {
    total: rawCameras.length,
    timelapse: rawCameras.filter((c) => (c.camera_type || "").toUpperCase().includes("TIMELAPSE")).length,
    live: rawCameras.filter((c) => (c.camera_type || "").toUpperCase().includes("LIVE")).length,
    ai: rawCameras.filter((c) => (c.camera_type || "").toUpperCase().includes("AI")).length,
    taskDone: rawCameras.filter((c) => taskStatus[c.id]).length,
  };

  return (
    <Container maxWidth={false} className="py-2" sx={{ px: { xs: 2, md: 3 } }}>
      {/* Widelapse Standard Top Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Breadcrumb title="Cameras" subtitle="Camera Registry" />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {/* Quick summary badges */}
          <Chip
            size="small"
            label={`Total: ${stats.total}`}
            sx={{ fontWeight: 700, borderRadius: "8px", bgcolor: "rgba(0,0,0,0.05)" }}
          />
          <Chip
            size="small"
            label={`Timelapse: ${stats.timelapse}`}
            sx={{ fontWeight: 700, borderRadius: "8px", bgcolor: "rgba(255, 204, 51, 0.2)", color: "#b45309" }}
          />
          <Chip
            size="small"
            label={`Live: ${stats.live}`}
            sx={{ fontWeight: 700, borderRadius: "8px", bgcolor: "rgba(16, 185, 129, 0.15)", color: "#059669" }}
          />
          {stats.ai > 0 && (
            <Chip
              size="small"
              label={`AI: ${stats.ai}`}
              sx={{ fontWeight: 700, borderRadius: "8px", bgcolor: "rgba(168, 85, 247, 0.15)", color: "#7c3aed" }}
            />
          )}

          <Tooltip title="Click to customize Task Title or Clear Marks">
            <Chip
              size="small"
              icon={<BsCheckCircleFill size={11} color="#15803d" />}
              label={`${taskTitle}: ${stats.taskDone}/${stats.total}`}
              onClick={() => {
                setTempTaskTitle(taskTitle);
                setOpenTaskDialog(true);
              }}
              sx={{
                bgcolor: "rgba(34, 197, 94, 0.15)",
                color: "#15803d",
                fontWeight: 700,
                borderRadius: "8px",
                cursor: "pointer",
                "&:hover": { bgcolor: "rgba(34, 197, 94, 0.25)" },
              }}
            />
          </Tooltip>

          <Button
            variant="contained"
            size="small"
            onClick={handleExportPDF}
            startIcon={<BsFileEarmarkPdfFill size={13} />}
            sx={{
              backgroundColor: "#FFCC33",
              color: "#000",
              "&:hover": { backgroundColor: "#f5bc18" },
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              px: 1.8,
              height: "36px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
            }}
          >
            Export PDF
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={loadData}
            startIcon={<BsArrowCounterclockwise size={13} />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              px: 1.6,
              height: "36px",
              borderColor: "rgba(0,0,0,0.12)",
              color: "inherit",
              "&:hover": {
                borderColor: "#FFCC33",
                backgroundColor: "rgba(255, 204, 51, 0.15)",
                color: "#000",
              },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Main Container styled like Widelapse's standard cards */}
      <div className="card-bg p-4 md:p-5 rounded-xl shadow-[0px_4px_20px_0px_rgba(0,0,0,0.06)] border border-gray-100 flex flex-col">
        {/* Filter Toolbar */}
        <Box sx={{ mb: 2.5 }}>
          <Grid container spacing={1.5} alignItems="center">
            {/* Camera Type Filter */}
            <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel id="camera-type-select-label" sx={{ fontSize: "0.8rem" }}>Camera Type</InputLabel>
                <Select
                  labelId="camera-type-select-label"
                  value={selectedType}
                  label="Camera Type"
                  sx={{ fontSize: "0.8rem", borderRadius: "10px" }}
                  onChange={(e) => {
                    setSelectedType(e.target.value);
                    handlePageChange(1);
                  }}
                >
                  <MenuItem value="ALL" sx={{ fontSize: "0.8rem" }}>All Camera Types</MenuItem>
                  <MenuItem value="TIMELAPSE" sx={{ fontSize: "0.8rem" }}>Timelapse</MenuItem>
                  <MenuItem value="LIVECAMERA" sx={{ fontSize: "0.8rem" }}>Live Camera</MenuItem>
                  <MenuItem value="LIVETIMELAPSE" sx={{ fontSize: "0.8rem" }}>Live Timelapse</MenuItem>
                  <MenuItem value="AICAMERA" sx={{ fontSize: "0.8rem" }}>AI Camera</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Company Filter */}
            <Grid item xs={12} sm={6} md={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel id="company-select-label" sx={{ fontSize: "0.8rem" }}>Company Name</InputLabel>
                <Select
                  labelId="company-select-label"
                  value={selectedCompany}
                  label="Company Name"
                  sx={{ fontSize: "0.8rem", borderRadius: "10px" }}
                  onChange={(e) => {
                    setSelectedCompany(e.target.value);
                    handlePageChange(1);
                  }}
                >
                  <MenuItem value="ALL" sx={{ fontSize: "0.8rem" }}>All Companies</MenuItem>
                  {companiesList.map((comp) => (
                    <MenuItem key={comp.id} value={comp.company_name || comp.id} sx={{ fontSize: "0.8rem" }}>
                      {comp.company_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Task Status Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="pw-status-select-label" sx={{ fontSize: "0.8rem" }}>{taskTitle} Filter</InputLabel>
                <Select
                  labelId="pw-status-select-label"
                  value={pwStatusFilter}
                  label={`${taskTitle} Filter`}
                  sx={{ fontSize: "0.8rem", borderRadius: "10px" }}
                  onChange={(e) => {
                    setPwStatusFilter(e.target.value);
                    handlePageChange(1);
                  }}
                >
                  <MenuItem value="ALL" sx={{ fontSize: "0.8rem" }}>All Status</MenuItem>
                  <MenuItem value="CHANGED" sx={{ fontSize: "0.8rem" }}>✓ Done Only</MenuItem>
                  <MenuItem value="PENDING" sx={{ fontSize: "0.8rem" }}>Pending Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Camera Name / Search Query */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search camera, IP, username..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  handlePageChange(1);
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: "rgba(0,0,0,0.02)",
                  },
                }}
                inputProps={{ style: { fontSize: "0.8rem" } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BsSearch color="#94a3b8" size={13} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* View Switcher & Reset */}
            <Grid item xs={12} sm={12} md={2} sx={{ display: "flex", gap: 1, justifyContent: "flex-end", alignItems: "center" }}>
              <Tooltip title="Reset Filters">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleResetFilters}
                  sx={{
                    minWidth: 36,
                    height: 36,
                    px: 1,
                    borderRadius: "10px",
                    borderColor: "rgba(0,0,0,0.12)",
                    color: "inherit",
                    "&:hover": { borderColor: "#FFCC33", backgroundColor: "rgba(255, 204, 51, 0.15)" },
                  }}
                >
                  <BsArrowCounterclockwise size={14} />
                </Button>
              </Tooltip>

              <ToggleButtonGroup
                size="small"
                value={viewMode}
                exclusive
                onChange={(e, nextView) => nextView && setViewMode(nextView)}
                sx={{
                  borderRadius: "10px",
                  "& .MuiToggleButton-root.Mui-selected": {
                    backgroundColor: "#FFCC33",
                    color: "#000",
                    fontWeight: 700,
                  },
                }}
              >
                <ToggleButton value="table" aria-label="table view" sx={{ px: 1.2, height: 36, borderRadius: "10px 0 0 10px" }}>
                  <BsListUl size={14} />
                </ToggleButton>
                <ToggleButton value="grid" aria-label="grid view" sx={{ px: 1.2, height: 36, borderRadius: "0 10px 10px 0" }}>
                  <BsGridFill size={13} />
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          </Grid>

          {/* Filter Summary info */}
          <Box sx={{ mt: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500, fontSize: "0.75rem" }}>
              Showing <strong>{filteredCameras.length}</strong> of <strong>{rawCameras.length}</strong> cameras
              {stats.taskDone > 0 && ` (${taskTitle} Done: ${stats.taskDone})`}
            </Typography>
            <Button
              size="small"
              startIcon={<BsPencilFill size={11} />}
              onClick={() => {
                setTempTaskTitle(taskTitle);
                setOpenTaskDialog(true);
              }}
              sx={{
                textTransform: "none",
                fontSize: "0.72rem",
                py: 0.3,
                px: 1,
                color: "inherit",
                fontWeight: 600,
                borderRadius: "8px",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
              }}
            >
              Customize Task / Column ({taskTitle})
            </Button>
          </Box>
        </Box>

        {/* Camera Data Content */}
        {viewMode === "table" ? (
          <TableContainer
            component={Paper}
            className="card-bg"
            sx={{
              borderRadius: "12px",
              boxShadow: "none",
              border: "1px solid rgba(0,0,0,0.06)",
              overflowX: "auto",
              width: "100%",
            }}
          >
            <Table size="small" sx={{ width: "100%", minWidth: 720 }} aria-label="camera registry table">
              <TableHead sx={{ backgroundColor: "rgba(0,0,0,0.03)", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", width: 30, py: 1, px: 0.8, textAlign: "center" }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", py: 1, px: 0.8 }}>Camera Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", py: 1, px: 0.8 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", py: 1, px: 0.8 }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", py: 1, px: 0.8 }}>IP Address & Port</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", py: 1, px: 0.8 }}>Username</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", py: 1, px: 0.8 }}>Password</TableCell>
                  
                  {/* Customizable / Editable Column Header */}
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase", py: 1, px: 0.8, textAlign: "center", width: 110 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.4 }}>
                    <span title="Click pencil to edit column name">{taskTitle}</span>
                    <Tooltip title="Edit Column / Task Name">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setTempTaskTitle(taskTitle);
                          setOpenTaskDialog(true);
                        }}
                        sx={{ p: 0.2, color: "#0284c7", "&:hover": { bgcolor: "rgba(2, 132, 199, 0.1)" } }}
                      >
                        <BsPencilFill size={9} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>

                <TableCell sx={{ fontWeight: 800, color: "#0c2a45", fontSize: "0.68rem", textTransform: "uppercase", py: 0.8, px: 0.8 }}>Brand</TableCell>
                <TableCell sx={{ fontWeight: 800, color: "#0c2a45", fontSize: "0.68rem", textTransform: "uppercase", py: 0.8, px: 0.6, textAlign: "center", width: 50 }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 800, color: "#0c2a45", fontSize: "0.68rem", textTransform: "uppercase", width: 45, py: 0.8, px: 0.6 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCameras.length > 0 ? (
                paginatedCameras.map((cam, idx) => {
                  const uid = cam._uid || `cam_${cam.id}_${idx}`;
                  const isPwVisible = visiblePasswords[uid];
                  const isTaskDone = Boolean(taskStatus[cam.id]);
                  const fullUrl = buildCameraWebUrl(cam);
                  const rowNum = (page - 1) * pageSize + idx + 1;

                  return (
                    <TableRow
                      key={uid}
                      hover
                      sx={{
                        borderBottom: "1px solid #f1f5f9",
                        bgcolor: isTaskDone ? "rgba(240, 253, 244, 0.45)" : "inherit",
                        "&:hover": { bgcolor: isTaskDone ? "rgba(240, 253, 244, 0.85)" : "#f8fafc" },
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      <TableCell sx={{ color: "#94a3b8", fontWeight: 700, fontSize: "0.68rem", py: 0.4, px: 0.6, textAlign: "center" }}>{rowNum}</TableCell>
                      
                      {/* Camera Name */}
                      <TableCell sx={{ py: 0.4, px: 0.8 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                          <Avatar
                            src={cam.thumbnail || cam.image}
                            alt={cam.camera_name}
                            sx={{ width: 24, height: 24, bgcolor: "#f1f5f9", border: "1px solid #e2e8f0" }}
                          >
                            <BsCameraVideoFill size={11} color="#0c2a45" />
                          </Avatar>
                          <Box sx={{ maxWidth: 160, overflow: "hidden" }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: "#0c2a45", fontSize: "0.72rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={cam.camera_name}>
                              {cam.camera_name}
                            </Typography>
                            {cam.project_name && (
                              <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.64rem", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={cam.project_name}>
                                {cam.project_name}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Camera Type */}
                      <TableCell sx={{ py: 0.4, px: 0.8 }}>{getTypeChip(cam.camera_type)}</TableCell>

                      {/* Company Name */}
                      <TableCell sx={{ py: 0.4, px: 0.8 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155", fontSize: "0.70rem", maxWidth: 130, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={cam.company_name}>
                          {cam.company_name || "-"}
                        </Typography>
                      </TableCell>

                      {/* IP Address */}
                      <TableCell sx={{ py: 0.4, px: 0.8 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                          <Typography
                            component="a"
                            href={fullUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#0c2a45",
                              fontFamily: "monospace",
                              fontSize: "0.70rem",
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline", color: "#0369a1" },
                            }}
                          >
                            {cam.ip_address || "N/A"}
                            {cam.http_port && !["80", "443", ""].includes(String(cam.http_port)) ? `:${cam.http_port}` : ""}
                          </Typography>
                          {cam.ip_address && (
                            <Tooltip title="Open Camera Page">
                              <IconButton
                                size="small"
                                component="a"
                                href={fullUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ p: 0.1, color: "#64748b" }}
                              >
                                <BsBoxArrowUpRight size={9} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>

                      {/* Username */}
                      <TableCell sx={{ py: 0.4, px: 0.8 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.2 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 600,
                              bgcolor: "#f8fafc",
                              border: "1px solid #e2e8f0",
                              color: "#0f172a",
                              px: 0.5,
                              py: 0.1,
                              borderRadius: 0.8,
                              fontSize: "0.68rem",
                            }}
                          >
                            {cam.username || "-"}
                          </Typography>
                          {cam.username && (
                            <Tooltip title={copiedId === `u_${uid}` ? "Copied!" : "Copy Username"}>
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(cam.username, `u_${uid}`)}
                                sx={{ p: 0.1, color: copiedId === `u_${uid}` ? "#10b981" : "#94a3b8" }}
                              >
                                {copiedId === `u_${uid}` ? <BsCheck2 size={11} /> : <BsClipboard size={9} />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>

                      {/* Password */}
                      <TableCell sx={{ py: 0.4, px: 0.8 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.2 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 600,
                              bgcolor: "#f8fafc",
                              border: "1px solid #e2e8f0",
                              color: "#0f172a",
                              px: 0.5,
                              py: 0.1,
                              borderRadius: 0.8,
                              fontSize: "0.68rem",
                              letterSpacing: isPwVisible ? "normal" : "1px",
                            }}
                          >
                            {cam.password ? (isPwVisible ? cam.password : "••••••") : "-"}
                          </Typography>

                          {cam.password && (
                            <>
                              <Tooltip title={isPwVisible ? "Hide password" : "Show password"}>
                                <IconButton
                                  size="small"
                                  onClick={() => togglePasswordVisibility(uid)}
                                  sx={{ p: 0.1, color: "#64748b" }}
                                >
                                  {isPwVisible ? <BsEyeSlashFill size={10} /> : <BsEyeFill size={10} />}
                                </IconButton>
                              </Tooltip>

                              <Tooltip title={copiedId === `p_${uid}` ? "Copied!" : "Copy Password"}>
                                <IconButton
                                  size="small"
                                  onClick={() => copyToClipboard(cam.password, `p_${uid}`)}
                                  sx={{ p: 0.1, color: copiedId === `p_${uid}` ? "#10b981" : "#94a3b8" }}
                                >
                                  {copiedId === `p_${uid}` ? <BsCheck2 size={11} /> : <BsClipboard size={9} />}
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>

                      {/* Interactive Task Status Marker */}
                      <TableCell sx={{ py: 0.4, px: 0.8, textAlign: "center" }}>
                        <Tooltip title={isTaskDone ? `Click to mark as Pending` : `Click to mark as ${taskTitle}`}>
                          <Chip
                            size="small"
                            icon={isTaskDone ? <BsCheckCircleFill size={9} color="#15803d" /> : undefined}
                            label={isTaskDone ? "✓ Done" : "Pending"}
                            onClick={() => toggleTaskStatus(cam.id)}
                            sx={{
                              bgcolor: isTaskDone ? "#dcfce7" : "#f1f5f9",
                              color: isTaskDone ? "#15803d" : "#64748b",
                              border: isTaskDone ? "1px solid #86efac" : "1px solid #cbd5e1",
                              fontWeight: 700,
                              fontSize: "0.64rem",
                              cursor: "pointer",
                              height: 20,
                              px: 0.4,
                              "&:hover": {
                                bgcolor: isTaskDone ? "#bbf7d0" : "#e2e8f0",
                              },
                            }}
                          />
                        </Tooltip>
                      </TableCell>

                      {/* Brand */}
                      <TableCell sx={{ py: 0.4, px: 0.8 }}>
                        <Typography variant="body2" sx={{ color: "#475569", fontWeight: 500, fontSize: "0.70rem" }}>
                          {cam.brand_name || "-"}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell sx={{ py: 0.4, px: 0.6, textAlign: "center" }}>
                        <Chip
                          size="small"
                          label={cam.active !== false ? "Active" : "Off"}
                          sx={{
                            bgcolor: cam.active !== false ? "#ecfdf5" : "#f1f5f9",
                            color: cam.active !== false ? "#059669" : "#64748b",
                            fontWeight: 700,
                            fontSize: "0.64rem",
                            height: 18,
                            borderRadius: 1,
                          }}
                        />
                      </TableCell>

                      {/* Action */}
                      <TableCell align="center" sx={{ py: 0.4, px: 0.6 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          component="a"
                          href={fullUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            textTransform: "none",
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            py: 0.1,
                            px: 1,
                            borderRadius: "8px",
                            borderColor: "rgba(0,0,0,0.12)",
                            color: "inherit",
                            "&:hover": {
                              borderColor: "#FFCC33",
                              backgroundColor: "rgba(255, 204, 51, 0.15)",
                              color: "#000",
                            },
                          }}
                        >
                          Visit
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500, fontSize: "0.78rem" }}>
                      No cameras found matching your selected filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        /* Grid Card View */
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 2,
          }}
        >
          {paginatedCameras.length > 0 ? (
            paginatedCameras.map((cam, idx) => {
              const uid = cam._uid || `cam_${cam.id}_${idx}`;
              const isPwVisible = visiblePasswords[uid];
              const isTaskDone = Boolean(taskStatus[cam.id]);
              const fullUrl = buildCameraWebUrl(cam);
              const previewImg = cam.thumbnail || cam.image;

              return (
                <Card
                  key={uid}
                  className="card-bg"
                  sx={{
                    borderRadius: "14px",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
                    border: isTaskDone ? "1px solid #86efac" : "1px solid rgba(0,0,0,0.06)",
                    bgcolor: isTaskDone ? "rgba(34, 197, 94, 0.08)" : undefined,
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                  }}
                >
                  {/* Card Header Media & Badges */}
                  <Box sx={{ position: "relative", height: 110, bgcolor: "#0f172a", overflow: "hidden" }}>
                    {previewImg ? (
                      <Box
                        component="img"
                        src={previewImg}
                        alt={cam.camera_name}
                        sx={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.85 }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "#1e293b",
                        }}
                      >
                        <BsCameraVideoFill size={36} color="#64748b" />
                      </Box>
                    )}

                    {/* Type Badge on Top-Left */}
                    <Box sx={{ position: "absolute", top: 8, left: 8 }}>{getTypeChip(cam.camera_type)}</Box>

                    {/* Status Badge on Top-Right */}
                    <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                      <Chip
                        size="small"
                        label={cam.active !== false ? "Active" : "Off"}
                        sx={{
                          bgcolor: cam.active !== false ? "#10b981" : "#64748b",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: "0.68rem",
                          height: 20,
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Card Main Info */}
                  <CardContent sx={{ p: 1.5, flex: 1, display: "flex", flexDirection: "column" }}>
                    <Box sx={{ mb: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 800,
                          fontSize: "0.82rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={cam.camera_name}
                      >
                        {cam.camera_name}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.2 }}>
                        <BsBuilding size={11} color="#64748b" />
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#64748b",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {cam.company_name || "No Company"}
                          {cam.project_name ? ` • ${cam.project_name}` : ""}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Uniform Credential Container */}
                    <Box
                      sx={{
                        bgcolor: "rgba(0,0,0,0.02)",
                        border: "1px solid rgba(0,0,0,0.06)",
                        borderRadius: "10px",
                        p: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.8,
                        mb: 1.2,
                      }}
                    >
                      {/* IP Row */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.7rem" }}>
                          IP & Port:
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                          <Typography
                            component="a"
                            href={fullUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              color: "#0c2a45",
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline", color: "#0369a1" },
                            }}
                          >
                            {cam.ip_address || "N/A"}
                            {cam.http_port && !["80", "443", ""].includes(String(cam.http_port)) ? `:${cam.http_port}` : ""}
                          </Typography>
                          {cam.ip_address && (
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(cam.ip_address, `ip_${uid}`)}
                              sx={{ p: 0.2 }}
                            >
                              {copiedId === `ip_${uid}` ? <BsCheck2 color="#10b981" size={11} /> : <BsClipboard size={9} color="#94a3b8" />}
                            </IconButton>
                          )}
                        </Box>
                      </Box>

                      {/* Username Row */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.7rem" }}>
                          Username:
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                          <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: "0.72rem", color: "#0f172a" }}>
                            {cam.username || "-"}
                          </Typography>
                          {cam.username && (
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(cam.username, `u_${uid}`)}
                              sx={{ p: 0.2 }}
                            >
                              {copiedId === `u_${uid}` ? <BsCheck2 color="#10b981" size={11} /> : <BsClipboard size={9} color="#94a3b8" />}
                            </IconButton>
                          )}
                        </Box>
                      </Box>

                      {/* Password Row */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.7rem" }}>
                          Password:
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              color: "#0f172a",
                              letterSpacing: isPwVisible ? "normal" : "1.5px",
                            }}
                          >
                            {cam.password ? (isPwVisible ? cam.password : "••••••") : "-"}
                          </Typography>
                          {cam.password && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => togglePasswordVisibility(uid)}
                                sx={{ p: 0.2, color: "#64748b" }}
                              >
                                {isPwVisible ? <BsEyeSlashFill size={11} /> : <BsEyeFill size={11} />}
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => copyToClipboard(cam.password, `p_${uid}`)}
                                sx={{ p: 0.2 }}
                              >
                                {copiedId === `p_${uid}` ? <BsCheck2 color="#10b981" size={11} /> : <BsClipboard size={9} color="#94a3b8" />}
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </Box>

                      {/* Dynamic Task Marker on Card */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 0.3, borderTop: "1px dashed rgba(0,0,0,0.08)" }}>
                        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.68rem" }}>
                          {taskTitle}:
                        </Typography>
                        <Chip
                          size="small"
                          icon={isTaskDone ? <BsCheckCircleFill size={9} color="#15803d" /> : undefined}
                          label={isTaskDone ? "✓ Done" : "Mark Done"}
                          onClick={() => toggleTaskStatus(cam.id)}
                          sx={{
                            bgcolor: isTaskDone ? "#dcfce7" : "rgba(0,0,0,0.04)",
                            color: isTaskDone ? "#15803d" : "#64748b",
                            border: isTaskDone ? "1px solid #86efac" : "1px solid rgba(0,0,0,0.1)",
                            fontWeight: 700,
                            fontSize: "0.65rem",
                            cursor: "pointer",
                            height: 20,
                            borderRadius: "6px",
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Card Bottom Actions */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: "auto", pt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.7rem" }}>
                        Brand: {cam.brand_name || "Generic"}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        component="a"
                        href={fullUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          textTransform: "none",
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          borderRadius: "8px",
                          py: 0.2,
                          px: 1.2,
                          borderColor: "rgba(0,0,0,0.12)",
                          color: "inherit",
                          "&:hover": {
                            borderColor: "#FFCC33",
                            backgroundColor: "rgba(255, 204, 51, 0.15)",
                            color: "#000",
                          },
                        }}
                      >
                        Open Camera
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Paper className="card-bg" sx={{ p: 4, textAlign: "center", borderRadius: "12px", gridColumn: "1 / -1", border: "1px solid rgba(0,0,0,0.06)" }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                No cameras found matching your selected filters.
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      {/* Pagination Footer */}
      <Box
        sx={{
          mt: 2.5,
          pt: 2,
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1.5,
          borderTop: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600, fontSize: "0.75rem" }}>
            Rows per page:
          </Typography>
          <FormControl size="small">
            <Select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              sx={{ fontSize: "0.75rem", height: 28, minWidth: 65, borderRadius: "8px" }}
            >
              <MenuItem value={10} sx={{ fontSize: "0.75rem" }}>10</MenuItem>
              <MenuItem value={25} sx={{ fontSize: "0.75rem" }}>25</MenuItem>
              <MenuItem value={50} sx={{ fontSize: "0.75rem" }}>50</MenuItem>
              <MenuItem value={100} sx={{ fontSize: "0.75rem" }}>100</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.72rem" }}>
            (Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, filteredCameras.length)} of {filteredCameras.length})
          </Typography>
        </Box>

        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, val) => handlePageChange(val)}
          shape="rounded"
          size="small"
          sx={{
            "& .Mui-selected": {
              backgroundColor: "#FFCC33 !important",
              color: "#000 !important",
              fontWeight: 700,
            },
          }}
        />
      </Box>
      </div>

      {/* Task Title & Customization Dialog using Widelapse standard RDialog */}
      <RDialog
        open={openTaskDialog}
        handleClose={() => setOpenTaskDialog(false)}
        title="Customize Task / Column Name"
      >
        <Box sx={{ minWidth: { xs: 280, sm: 380 }, py: 1 }}>
          <Typography variant="caption" sx={{ color: "#64748b", display: "block", mb: 2 }}>
            Set a predefined or custom task name for this column. Saved locally in your browser so it stays across reloads.
          </Typography>

          <TextField
            fullWidth
            size="small"
            label="Column / Task Title"
            value={tempTaskTitle}
            onChange={(e) => setTempTaskTitle(e.target.value)}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            inputProps={{ style: { fontSize: "0.85rem", fontWeight: 600 } }}
          />

          <Typography variant="caption" sx={{ fontWeight: 700, color: "inherit", display: "block", mb: 1 }}>
            Quick Presets:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 2 }}>
            {PRESET_TASKS.map((preset) => {
              const isSelected = tempTaskTitle === preset;
              return (
                <Chip
                  key={preset}
                  label={preset}
                  size="small"
                  onClick={() => setTempTaskTitle(preset)}
                  sx={{
                    cursor: "pointer",
                    fontSize: "0.72rem",
                    fontWeight: isSelected ? 700 : 500,
                    bgcolor: isSelected ? "#FFCC33" : "rgba(0,0,0,0.04)",
                    color: isSelected ? "#000000" : "inherit",
                    borderRadius: "8px",
                    "&:hover": { bgcolor: isSelected ? "#f5bc18" : "rgba(255, 204, 51, 0.2)" },
                  }}
                />
              );
            })}
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="caption" sx={{ color: "#dc2626", fontWeight: 600 }}>
              Need a fresh start?
            </Typography>
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<BsTrashFill size={11} />}
              onClick={handleClearTaskMarks}
              sx={{ textTransform: "none", fontSize: "0.72rem", py: 0.3, borderRadius: "8px" }}
            >
              Clear All Marks ({stats.taskDone})
            </Button>
          </Box>

          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <Button
              size="small"
              onClick={() => setOpenTaskDialog(false)}
              sx={{ textTransform: "none", color: "#64748b", fontWeight: 600, borderRadius: "8px" }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => handleSaveTaskTitle(tempTaskTitle)}
              sx={{
                bgcolor: "#FFCC33",
                color: "#000",
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                px: 2.5,
                height: "36px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                "&:hover": { bgcolor: "#f5bc18" },
              }}
            >
              Save Task Name
            </Button>
          </Box>
        </Box>
      </RDialog>
    </Container>
  );
}
